import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

/* ============================================================
   EEVEE.JS
   Pasaporte Digital de Eevee Castañeda Ughetti
============================================================ */

const PET = {
  name: "Eevee",
  lastName: "Castañeda Ughetti",
  fullName: "Eevee Castañeda Ughetti",
  birth: "13 de abril de 2019",
  age: "7 años",
  breed: "Pinscher mediano",
  coat: "Negro con blanco",
  sex: "Hembra",
  chip: "900217001633874",
  vaccines: "5 de agosto de 2026",
  city: "Medellín, Antioquia",

  responsible: {
    name: "Jenny Ughetti Valencia",
    shortName: "Jenny",
    phones: ["3007503796", "3244967629"],
  },

  emergency: {
    name: "Andres Castañeda Esguerra",
    shortName: "Andrés",
    phones: ["3234000710", "3114044203"],
  },
};

const PHOTO_URL =
  "https://zaphy.com.co/wp-content/uploads/2026/08/WhatsApp-Image-2026-08-10-at-8.31.09-PM.jpeg";

const sections = [
  ["inicio", "⌂", "INICIO"],
  ["perfil", "🐶", "PERFIL"],
  ["alimentacion", "🍽️", "ALIMENTACIÓN"],
  ["salud", "🩺", "SALUD"],
  ["rutina", "🕐", "RUTINA"],
  ["seguridad", "🛡️", "SEGURIDAD"],
  ["contactos", "📞", "CONTACTOS"],
];

const personality = [
  ["🌡️", "Muy friolenta", "Necesita un lugar cálido y cómodo para descansar."],
  ["🤫", "No le gusta que la molesten", "Respeta sus momentos de descanso y tranquilidad."],
  ["📢", "Muy bullosa", "Puede ladrar y azarar a perros grandes."],
  ["❤️", "Depende del cariño", "La compañía y el afecto son muy importantes para ella."],
  ["🥺", "Necesita compañía", "Si no recibe cariño puede deprimirse y quedarse en un rincón."],
  ["💧", "No le gusta mojarse las patitas", "Evitar obligarla a caminar por zonas mojadas."],
  ["🌳", "Cuidado en potreros", "Puede perderse en espacios abiertos sin límites."],
];

const meals = [
  ["🌅", "7:00 a. m.", "2 cucharadas soperas grandes de comida natural + 5 granos de concentrado."],
  ["☀️", "2:00 p. m.", "2 cucharadas soperas grandes de comida natural + 5 granos de concentrado."],
  ["🌙", "6:00 p. m.", "1 cucharada sopera de comida natural."],
];

const safety = [
  ["🌳", "Potreros", "No dejarla libre en espacios abiertos sin límites. Puede perderse."],
  ["🚪", "Puertas y portones", "Verificar dónde está Eevee antes de abrir puertas o portones."],
  ["🦮", "Paseos", "Supervisar sus salidas y utilizar sus elementos de seguridad."],
  ["🥶", "Frío", "Procurar un lugar cálido y protegido."],
  ["💧", "Patitas", "No obligarla a caminar por zonas mojadas."],
  ["🐕", "Perros grandes", "Supervisar sus interacciones porque puede reaccionar con intensidad."],
];

/* ============================================================
   CSS
============================================================ */

const CSS = `
:root{
  --ee-navy:#082C4C;
  --ee-navy2:#123D64;
  --ee-lime:#DCE900;
  --ee-pink:#EF5B83;
  --ee-purple:#9C5BD6;
  --ee-soft:#F8F8F4;
  --ee-cream:#FFFDF6;
  --ee-line:#E5E8E2;
  --ee-text:#172433;
  --ee-muted:#687583;
  --ee-yellow:#FFF9DC;
  --ee-blue:#EFF7FD;
  --ee-red:#FFF1F1;
  --ee-shadow:0 10px 30px rgba(8,44,76,.10);
}

*{box-sizing:border-box}
html{scroll-behavior:smooth}

.ee-app{
  width:100%;
  min-height:100vh;
  margin:0;
  background:#fff;
  color:var(--ee-text);
  font-family:Inter,Arial,Helvetica,sans-serif;
}

.ee-app button,.ee-app a{font:inherit}
.ee-app button{cursor:pointer}
.ee-app a{text-decoration:none}

/* HEADER */

.ee-top{
  width:100%;
  height:76px;
  position:sticky;
  top:0;
  z-index:999;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:20px;
  padding:0 32px;
  background:var(--ee-navy);
  box-shadow:0 4px 20px rgba(0,0,0,.16);
}

.ee-brand{
  display:flex;
  align-items:center;
  gap:11px;
  border:0;
  padding:0;
  background:transparent;
  color:white;
}

.ee-brand-paw{
  width:42px;
  height:42px;
  display:grid;
  place-items:center;
  color:var(--ee-lime);
  font-size:31px;
}

.ee-brand-text{text-align:left}

.ee-brand-text strong{
  display:block;
  color:white;
  font-size:20px;
  font-weight:900;
  line-height:1;
}

.ee-brand-text small{
  display:block;
  margin-top:4px;
  color:var(--ee-lime);
  font-size:10px;
  font-weight:700;
}

.ee-nav{
  display:flex;
  align-items:stretch;
  gap:2px;
  height:76px;
}

.ee-nav button{
  min-width:76px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:5px;
  padding:8px 10px;
  border:0;
  border-bottom:3px solid transparent;
  background:transparent;
  color:white;
  font-size:10px;
  font-weight:900;
  transition:.2s ease;
}

.ee-nav button .icon{font-size:19px}

.ee-nav button:hover,
.ee-nav button.active{
  color:var(--ee-lime);
  border-bottom-color:var(--ee-lime);
  background:rgba(255,255,255,.04);
}

/* HERO */

.ee-hero{
  position:relative;
  min-height:500px;
  overflow:hidden;
  background:var(--ee-navy);
}

.ee-hero-photo{
  position:absolute;
  inset:0;
}

.ee-hero-photo img{
  width:100%;
  height:100%;
  object-fit:cover;
  object-position:30% 35%;
  transform:scaleX(-1);
}

.ee-hero-photo::after{
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(
    90deg,
    rgba(8,44,76,.98) 0%,
    rgba(8,44,76,.92) 38%,
    rgba(8,44,76,.30) 68%,
    rgba(8,44,76,.05) 100%
  );
}

.ee-hero-content{
  position:relative;
  z-index:3;
  width:min(1120px,calc(100% - 40px));
  min-height:500px;
  margin:auto;
  display:flex;
  flex-direction:column;
  justify-content:center;
  align-items:flex-start;
}

.ee-passport{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:10px 18px;
  border-radius:999px;
  background:var(--ee-lime);
  color:var(--ee-navy);
  font-size:12px;
  font-weight:900;
}

.ee-hero h1{
  margin:17px 0 0;
  color:white;
  font-size:clamp(65px,8vw,105px);
  font-weight:950;
  line-height:.95;
  letter-spacing:3px;
}

.ee-hero h2{
  margin:8px 0 0;
  color:var(--ee-lime);
  font-size:clamp(25px,3vw,40px);
  font-weight:500;
  font-style:italic;
}

.ee-hero p{
  max-width:470px;
  margin:25px 0 0;
  color:white;
  font-size:18px;
  line-height:1.55;
}

.ee-tags{
  display:flex;
  flex-wrap:wrap;
  gap:9px;
  margin-top:23px;
}

.ee-tags span{
  padding:9px 12px;
  border:1px solid rgba(255,255,255,.25);
  border-radius:999px;
  background:rgba(255,255,255,.1);
  color:white;
  font-size:12px;
  font-weight:700;
}

/* IDENTIFICACIÓN */

.ee-identification{
  position:relative;
  z-index:10;
  width:min(1120px,calc(100% - 40px));
  display:grid;
  grid-template-columns:repeat(5,1fr);
  margin:-34px auto 0;
  overflow:hidden;
  border:1px solid var(--ee-line);
  border-radius:21px;
  background:white;
  box-shadow:var(--ee-shadow);
}

.ee-id-item{
  min-height:115px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  padding:14px;
  text-align:center;
  border-right:1px solid var(--ee-line);
}

.ee-id-item:last-child{border-right:0}
.ee-id-icon{font-size:27px}

.ee-id-item small{
  margin-top:7px;
  color:var(--ee-navy);
  font-size:12px;
  font-weight:900;
}

.ee-id-item strong{
  margin-top:6px;
  color:var(--ee-text);
  font-size:13px;
  font-weight:500;
  line-height:1.35;
}

/* CONTENEDOR */

.ee-container{
  width:min(1120px,calc(100% - 40px));
  margin:auto;
}

.ee-home{padding:46px 0 70px}

.ee-section-title{
  margin:0 0 22px;
}

.ee-section-title h2{
  margin:0;
  color:var(--ee-navy);
  font-size:clamp(25px,3vw,34px);
}

.ee-section-title p{
  margin:6px 0 0;
  color:var(--ee-muted);
  font-size:14px;
}

/* CARDS HOME */

.ee-feature-grid{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:15px;
}

.ee-feature-card{
  min-height:205px;
  display:flex;
  flex-direction:column;
  align-items:center;
  padding:22px 18px;
  border:1px solid var(--ee-line);
  border-radius:20px;
  background:white;
  text-align:center;
  box-shadow:0 5px 17px rgba(8,44,76,.05);
  transition:.2s ease;
}

.ee-feature-card:hover{
  transform:translateY(-4px);
  box-shadow:var(--ee-shadow);
}

.ee-feature-icon{
  width:64px;
  height:64px;
  display:grid;
  place-items:center;
  border-radius:50%;
  background:linear-gradient(145deg,#FFDCE8,var(--ee-lime));
  font-size:29px;
}

.ee-feature-card h3{
  margin:13px 0 7px;
  color:var(--ee-navy);
  font-size:17px;
}

.ee-feature-card p{
  flex:1;
  margin:0;
  color:var(--ee-muted);
  font-size:12px;
  line-height:1.5;
}

.ee-feature-card b{
  margin-top:15px;
  padding:8px 20px;
  border-radius:8px;
  background:var(--ee-lime);
  color:var(--ee-navy);
  font-size:11px;
}

/* ME ENCONTRASTE */

.ee-found{
  display:grid;
  grid-template-columns:140px 1fr auto;
  align-items:center;
  gap:25px;
  margin-top:24px;
  padding:22px 28px;
  border-radius:24px;
  background:linear-gradient(100deg,var(--ee-navy),#06426D);
  color:white;
}

.ee-found-photo{
  width:120px;
  height:120px;
  object-fit:cover;
  border:5px solid white;
  border-radius:50%;
}

.ee-found h2{
  margin:0 0 6px;
  color:white;
  font-size:28px;
}

.ee-found h2 span{color:var(--ee-lime)}

.ee-found p{
  max-width:470px;
  margin:0;
  color:rgba(255,255,255,.9);
  font-size:14px;
  line-height:1.55;
}

.ee-found-buttons{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:10px;
}

.ee-found-button{
  min-width:190px;
  padding:15px 18px;
  border-radius:12px;
  text-align:center;
  font-size:12px;
  font-weight:900;
}

.ee-found-button.primary{
  background:var(--ee-lime);
  color:var(--ee-navy);
}

.ee-found-button.secondary{
  border:1px solid var(--ee-lime);
  color:white;
}

.ee-found-phone{
  display:block;
  margin-top:5px;
  font-size:15px;
}

/* PÁGINAS */

.ee-page{
  width:min(1080px,calc(100% - 36px));
  min-height:70vh;
  margin:auto;
  padding:35px 0 80px;
}

.ee-back{
  margin-bottom:20px;
  padding:8px 14px;
  border:1px solid var(--ee-line);
  border-radius:999px;
  background:white;
  color:var(--ee-navy);
  font-size:11px;
  font-weight:900;
}

.ee-page-head{
  margin-bottom:20px;
  padding:25px;
  border-radius:20px;
  background:var(--ee-navy);
  color:white;
}

.ee-page-head h1{
  margin:0;
  font-size:clamp(28px,4vw,40px);
}

.ee-page-head p{
  margin:6px 0 0;
  color:#DFE8EF;
  font-size:13px;
}

.ee-cards{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:14px;
}

.ee-card{
  padding:20px;
  border:1px solid var(--ee-line);
  border-radius:18px;
  background:white;
}

.ee-card .icon{font-size:31px}

.ee-card h3{
  margin:10px 0 6px;
  color:var(--ee-navy);
  font-size:16px;
}

.ee-card p{
  margin:0;
  color:var(--ee-muted);
  font-size:13px;
  line-height:1.55;
}

/* ALIMENTACIÓN */

.ee-meals{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:14px;
}

.ee-meal{
  padding:22px;
  border:1px solid var(--ee-line);
  border-radius:18px;
  background:var(--ee-cream);
}

.ee-meal .icon{font-size:30px}

.ee-meal strong{
  display:block;
  margin:9px 0 7px;
  color:var(--ee-navy);
  font-size:21px;
}

.ee-meal p{
  margin:0;
  color:var(--ee-muted);
  font-size:12px;
  line-height:1.55;
}

/* HEALTH */

.ee-health{
  display:grid;
  grid-template-columns:repeat(4,1fr);
  gap:14px;
}

/* CONTACT */

.ee-contact-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:18px;
}

.ee-contact{
  padding:23px;
  border:1px solid var(--ee-line);
  border-radius:20px;
  background:white;
}

.ee-contact.emergency{
  border-top:4px solid var(--ee-lime);
}

.ee-contact h3{
  margin:8px 0 12px;
  color:var(--ee-navy);
}

.ee-contact p{
  margin:6px 0;
  color:var(--ee-muted);
  font-size:12px;
}

.ee-actions{
  display:flex;
  gap:9px;
  margin-top:16px;
}

.ee-actions a{
  flex:1;
  padding:11px;
  border-radius:9px;
  text-align:center;
  font-size:11px;
  font-weight:900;
}

.ee-call{background:var(--ee-navy);color:white}
.ee-wa{background:var(--ee-lime);color:var(--ee-navy)}

/* NOTA */

.ee-note{
  margin-top:18px;
  padding:17px;
  border:1px solid #E8DF88;
  border-radius:16px;
  background:var(--ee-yellow);
  color:#4C5112;
  font-size:12px;
  line-height:1.55;
}

/* QR */

.ee-qr{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:25px;
  margin-top:20px;
  padding:23px;
  border:1px solid var(--ee-line);
  border-radius:20px;
  background:white;
}

.ee-qr h3{
  margin:0 0 7px;
  color:var(--ee-navy);
}

.ee-qr p{
  max-width:550px;
  color:var(--ee-muted);
  font-size:12px;
  line-height:1.5;
}

.ee-qr code{
  display:inline-block;
  padding:8px;
  border-radius:7px;
  background:var(--ee-soft);
  font-size:10px;
}

/* FOOTER */

.ee-footer{
  padding:30px 20px 35px;
  background:var(--ee-navy);
  color:white;
  text-align:center;
}

.ee-footer .paw{
  color:var(--ee-lime);
  font-size:32px;
}

.ee-footer strong{
  display:block;
  margin-top:5px;
  font-size:18px;
}

.ee-footer p{
  margin:6px 0 0;
  color:#DCE5ED;
  font-size:11px;
}

.ee-mobile-nav{display:none}

/* TABLET */

@media(max-width:1050px){
  .ee-nav{display:none}
  .ee-feature-grid{grid-template-columns:repeat(2,1fr)}
  .ee-cards{grid-template-columns:repeat(2,1fr)}
  .ee-health{grid-template-columns:repeat(2,1fr)}
  .ee-meals{grid-template-columns:repeat(2,1fr)}

  .ee-found{
    grid-template-columns:100px 1fr;
  }

  .ee-found-photo{
    width:95px;
    height:95px;
  }

  .ee-found-buttons{
    grid-column:1/-1;
  }

  .ee-mobile-nav{
    position:fixed;
    left:0;
    right:0;
    bottom:0;
    z-index:1000;
    display:grid;
    grid-template-columns:repeat(5,1fr);
    padding:6px 4px max(6px,env(safe-area-inset-bottom));
    border-top:1px solid var(--ee-line);
    background:rgba(255,255,255,.98);
    box-shadow:0 -5px 25px rgba(8,44,76,.08);
  }

  .ee-mobile-nav button{
    display:flex;
    flex-direction:column;
    align-items:center;
    gap:3px;
    padding:5px 2px;
    border:0;
    background:transparent;
    color:var(--ee-muted);
    font-size:17px;
  }

  .ee-mobile-nav button small{
    font-size:8px;
    font-weight:800;
  }

  .ee-mobile-nav button.active{
    color:var(--ee-navy);
  }

  .ee-footer{padding-bottom:90px}
}

/* MÓVIL */

@media(max-width:700px){
  .ee-top{
    height:64px;
    padding:0 16px;
  }

  .ee-brand-text strong{font-size:17px}
  .ee-brand-paw{width:34px;height:34px;font-size:26px}

  .ee-hero{
    min-height:560px;
  }

  .ee-hero-photo::after{
    background:linear-gradient(
      0deg,
      rgba(8,44,76,.99) 0%,
      rgba(8,44,76,.88) 35%,
      rgba(8,44,76,.08) 75%
    );
  }

  .ee-hero-content{
    width:calc(100% - 28px);
    min-height:560px;
    justify-content:flex-end;
    align-items:center;
    padding-bottom:40px;
    text-align:center;
  }

  .ee-hero h1{font-size:58px}
  .ee-hero h2{font-size:24px}
  .ee-hero p{font-size:14px}
  .ee-tags{justify-content:center}

  .ee-identification{
    width:100%;
    grid-template-columns:1fr 1fr;
    margin:0;
    border-radius:0;
  }

  .ee-id-item{
    min-height:105px;
    border-bottom:1px solid var(--ee-line);
  }

  .ee-id-item:nth-child(2n){border-right:0}

  .ee-id-item:last-child{
    grid-column:1/-1;
    border-bottom:0;
  }

  .ee-container,.ee-page{
    width:calc(100% - 28px);
  }

  .ee-feature-grid{
    grid-template-columns:1fr 1fr;
    gap:10px;
  }

  .ee-feature-card{
    min-height:190px;
    padding:17px 12px;
  }

  .ee-feature-card h3{font-size:14px}
  .ee-feature-card p{font-size:11px}

  .ee-found{
    grid-template-columns:72px 1fr;
    gap:14px;
    padding:17px;
  }

  .ee-found-photo{
    width:70px;
    height:70px;
    border-width:3px;
  }

  .ee-found h2{font-size:21px}
  .ee-found p{font-size:11px}

  .ee-found-buttons{
    grid-column:1/-1;
    grid-template-columns:1fr;
  }

  .ee-cards,
  .ee-health,
  .ee-meals,
  .ee-contact-grid{
    grid-template-columns:1fr;
  }

  .ee-actions{flex-direction:column}

  .ee-qr{
    flex-direction:column;
    align-items:flex-start;
  }
}

@media(max-width:420px){
  .ee-feature-grid{grid-template-columns:1fr}
  .ee-feature-card{min-height:auto}
}
`;

/* ============================================================
   CONTACTO
============================================================ */

function Contact({ person, emergency = false }) {
  const phone = person.phones[0];

  const message = encodeURIComponent(
    `Hola ${person.shortName}, encontré a Eevee. Estoy viendo su pasaporte digital.`
  );

  return (
    <article className={`ee-contact ${emergency ? "emergency" : ""}`}>
      <div style={{ fontSize: 30 }}>
        {emergency ? "🚨" : "❤️"}
      </div>

      <small>
        {emergency ? "CONTACTO DE EMERGENCIA" : "RESPONSABLE"}
      </small>

      <h3>{person.name}</h3>

      <p>📍 {PET.city}</p>

      {person.phones.map((number) => (
        <p key={number}>📞 {number}</p>
      ))}

      <div className="ee-actions">
        <a className="ee-call" href={`tel:+57${phone}`}>
          📞 Llamar
        </a>

        <a
          className="ee-wa"
          href={`https://wa.me/57${phone}?text=${message}`}
          target="_blank"
          rel="noreferrer"
        >
          💬 WhatsApp
        </a>
      </div>
    </article>
  );
}

/* ============================================================
   COMPONENTE PRINCIPAL
============================================================ */

function Eevee() {
  const [page, setPage] = useState("inicio");

  const qrUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return "https://zaphy.com.co/eevee";
    }

    return `${window.location.origin}/eevee`;
  }, []);

  const go = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ==========================================================
     HOME
  ========================================================== */

  const renderHome = () => {
    const cards = [
      ["perfil", "🐶", "Perfil y personalidad", "Conoce cómo soy y cómo acompañarme."],
      ["alimentacion", "🍽️", "Alimentación", "Mis horarios y cantidades de comida."],
      ["salud", "🩺", "Salud", "Mi identificación e información médica importante."],
      ["rutina", "🕐", "Rutina diaria", "Mis actividades durante el día."],
      ["seguridad", "🛡️", "Seguridad", "Cuidados especiales para mantenerme segura."],
      ["contactos", "📞", "Mis humanos", "Contactos de mi familia y emergencia."],
    ];

    const jennyPhone = PET.responsible.phones[0];
    const andresPhone = PET.emergency.phones[0];

    return (
      <>
        <section className="ee-hero">
          <div className="ee-hero-photo">
            <img src={PHOTO_URL} alt="Eevee" />
          </div>

          <div className="ee-hero-content">
            <span className="ee-passport">🐾 PASAPORTE DIGITAL</span>

            <h1>EEVEE</h1>
            <h2>Castañeda Ughetti</h2>

            <p>
              Todo lo que necesitas saber para cuidarme,
              mantenerme segura y darme mucho amor. ❤️
            </p>

            <div className="ee-tags">
              <span>🐶 Pinscher mediano</span>
              <span>♀ Hembra</span>
              <span>🖤 Negro con blanco</span>
            </div>
          </div>
        </section>

        <section className="ee-identification">
          <div className="ee-id-item">
            <div className="ee-id-icon">📅</div>
            <small>Nacimiento</small>
            <strong>{PET.birth}</strong>
          </div>

          <div className="ee-id-item">
            <div className="ee-id-icon">🐕</div>
            <small>Raza</small>
            <strong>{PET.breed}</strong>
          </div>

          <div className="ee-id-item">
            <div className="ee-id-icon">♀️</div>
            <small>Sexo</small>
            <strong>{PET.sex}</strong>
          </div>

          <div className="ee-id-item">
            <div className="ee-id-icon">🖤</div>
            <small>Características</small>
            <strong>{PET.coat}</strong>
          </div>

          <div className="ee-id-item">
            <div className="ee-id-icon">🆔</div>
            <small>Chip</small>
            <strong>{PET.chip}</strong>
          </div>
        </section>

        <div className="ee-container ee-home">
          <div className="ee-section-title">
            <h2>🐾 Conoce a Eevee</h2>
            <p>Explora las secciones de mi pasaporte digital.</p>
          </div>

          <div className="ee-feature-grid">
            {cards.map(([id, icon, title, text]) => (
              <button
                key={id}
                type="button"
                className="ee-feature-card"
                onClick={() => go(id)}
              >
                <div className="ee-feature-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{text}</p>
                <b>Ver más</b>
              </button>
            ))}
          </div>

          <section className="ee-found">
            <img
              src={PHOTO_URL}
              alt="Eevee"
              className="ee-found-photo"
            />

            <div>
              <h2>
                ¿Me <span>encontraste?</span>
              </h2>

              <p>
                Si me ves sola o crees que estoy perdida,
                por favor comunícate inmediatamente con mi familia. ❤️
              </p>
            </div>

            <div className="ee-found-buttons">
              <a
                className="ee-found-button primary"
                href={`tel:+57${jennyPhone}`}
              >
                📞 LLAMAR A JENNY
                <span className="ee-found-phone">300 750 3796</span>
              </a>

              <a
                className="ee-found-button secondary"
                href={`tel:+57${andresPhone}`}
              >
                📞 LLAMAR A ANDRÉS
                <span className="ee-found-phone">323 400 0710</span>
              </a>
            </div>
          </section>
        </div>
      </>
    );
  };

  /* ==========================================================
     PÁGINAS INTERNAS
  ========================================================== */

  const pageInfo = {
    perfil: [
      "🐶",
      "Perfil y personalidad",
      "Así es Eevee y así debemos acompañarla.",
    ],

    alimentacion: [
      "🍽️",
      "Alimentación",
      "Horarios y preparación exacta.",
    ],

    salud: [
      "🩺",
      "Salud e identificación",
      "Información importante para sus cuidadores.",
    ],

    rutina: [
      "🕐",
      "Rutina diaria",
      "Una guía sencilla para acompañarla.",
    ],

    seguridad: [
      "🛡️",
      "Seguridad y cuidados",
      "Puntos especiales que todo cuidador debe conocer.",
    ],

    contactos: [
      "📞",
      "Mis humanos",
      "Contactos rápidos de la familia.",
    ],
  };

  const renderInternalPage = () => {
    const title = pageInfo[page];

    return (
      <div className="ee-page">
        <button className="ee-back" onClick={() => go("inicio")}>
          ← Volver al inicio
        </button>

        <div className="ee-page-head">
          <h1>
            {title[0]} {title[1]}
          </h1>

          <p>{title[2]}</p>
        </div>

        {/* PERFIL */}

        {page === "perfil" && (
          <>
            <div className="ee-cards">
              {personality.map(([icon, title, text]) => (
                <article className="ee-card" key={title}>
                  <div className="icon">{icon}</div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>

            <div className="ee-note">
              💜 <strong>Importante:</strong> Eevee disfruta mucho
              del cariño y de la compañía de su familia. Respeta
              sus momentos de descanso y evita obligarla a hacer
              cosas que la incomoden.
            </div>
          </>
        )}

        {/* ALIMENTACIÓN */}

        {page === "alimentacion" && (
          <>
            <div className="ee-meals">
              {meals.map(([icon, time, text]) => (
                <article className="ee-meal" key={time}>
                  <div className="icon">{icon}</div>
                  <strong>{time}</strong>
                  <p>{text}</p>
                </article>
              ))}
            </div>

            <div className="ee-note">
              💜 <strong>Importante:</strong> a la preparación NO se
              le agrega agua. A las 7:00 a. m. y 2:00 p. m. recibe
              2 cucharadas soperas grandes de comida natural +
              5 granos de concentrado. A las 6:00 p. m. recibe
              1 cucharada sopera de comida natural.
            </div>
          </>
        )}

        {/* SALUD */}

        {page === "salud" && (
          <>
            <div className="ee-health">
              {[
                ["🆔", "CHIP", PET.chip],
                ["💉", "VACUNAS", PET.vaccines],
                ["🎂", "NACIMIENTO", PET.birth],
                ["🐾", "RAZA / PELAJE", `${PET.breed} · ${PET.coat}`],
              ].map(([icon, title, text]) => (
                <article className="ee-card" key={title}>
                  <div className="icon">{icon}</div>
                  <h3>{title}</h3>
                  <p><strong>{text}</strong></p>
                </article>
              ))}
            </div>

            <div className="ee-note">
              ⚠️ Eevee necesita comer regularmente. Ante síntomas
              digestivos, decaimiento o cambios importantes de
              comportamiento, informar a la familia.
            </div>
          </>
        )}

        {/* RUTINA */}

        {page === "rutina" && (
          <div className="ee-cards">
            <article className="ee-card">
              <div className="icon">🌅</div>
              <h3>Mañana</h3>
              <p>
                🍽️ 7:00 a. m. · comida<br />
                💧 Agua disponible<br />
                🦮 Actividad / paseo<br />
                ❤️ Cariño y compañía<br />
                😴 Descanso
              </p>
            </article>

            <article className="ee-card">
              <div className="icon">☀️</div>
              <h3>Tarde</h3>
              <p>
                🍽️ 2:00 p. m. · comida<br />
                💧 Agua disponible<br />
                🐾 Actividad y compañía<br />
                👀 Supervisión<br />
                😴 Descanso
              </p>
            </article>

            <article className="ee-card">
              <div className="icon">🌙</div>
              <h3>Noche</h3>
              <p>
                🍽️ 6:00 p. m. · 1 cucharada de comida natural<br />
                💜 Ambiente cálido<br />
                ❤️ Cariño<br />
                🚪 Revisar salidas<br />
                😴 Descanso
              </p>
            </article>
          </div>
        )}

        {/* SEGURIDAD */}

        {page === "seguridad" && (
          <>
            <div
              className="ee-note"
              style={{
                marginTop: 0,
                marginBottom: 18,
                background: "#FFF1F1",
                borderColor: "#F3CCCC",
                color: "#A84242",
              }}
            >
              🚨 <strong>Punto crítico:</strong> Eevee puede perderse
              en un potrero o espacio abierto sin límites.
              No dejarla sin supervisión.
            </div>

            <div className="ee-cards">
              {safety.map(([icon, title, text]) => (
                <article className="ee-card" key={title}>
                  <div className="icon">{icon}</div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </>
        )}

        {/* CONTACTOS */}

        {page === "contactos" && (
          <>
            <div className="ee-contact-grid">
              <Contact person={PET.responsible} />
              <Contact person={PET.emergency} emergency />
            </div>

            <div className="ee-qr">
              <div>
                <h3>🪪 QR del Pasaporte Digital</h3>

                <p>
                  Este código lleva directamente al pasaporte digital
                  de Eevee y será el que podremos utilizar en su carnet físico.
                </p>

                <code>{qrUrl}</code>
              </div>

              <QRCodeSVG
                value={qrUrl}
                size={170}
                level="H"
                includeMargin
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderPage = () => {
    if (page === "inicio") return renderHome();
    return renderInternalPage();
  };

  /* ==========================================================
     HTML PRINCIPAL
  ========================================================== */

  return (
    <div className="ee-app">
      <style>{CSS}</style>

      <header className="ee-top">
        <button
          type="button"
          className="ee-brand"
          onClick={() => go("inicio")}
        >
          <span className="ee-brand-paw">🐾</span>

          <span className="ee-brand-text">
            <strong>EEVEE</strong>
            <small>Pasaporte Digital</small>
          </span>
        </button>

        <nav className="ee-nav">
          {sections.map(([id, icon, label]) => (
            <button
              type="button"
              key={id}
              className={page === id ? "active" : ""}
              onClick={() => go(id)}
            >
              <span className="icon">{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </header>

      <main>
        {renderPage()}
      </main>

      <footer className="ee-footer">
        <div className="paw">🐾</div>
        <strong>Eevee Castañeda Ughetti</strong>
        <p>Pasaporte Digital · Identificación · Cuidados · Seguridad</p>
        <p>Hecho con amor por mi familia ❤️</p>
      </footer>

      <nav className="ee-mobile-nav">
        {[
          ["inicio", "⌂", "Inicio"],
          ["perfil", "🐶", "Perfil"],
          ["alimentacion", "🍽️", "Comida"],
          ["salud", "🩺", "Salud"],
          ["contactos", "☎", "Contacto"],
        ].map(([id, icon, label]) => (
          <button
            type="button"
            key={id}
            className={page === id ? "active" : ""}
            onClick={() => go(id)}
          >
            <span>{icon}</span>
            <small>{label}</small>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default Eevee;